import { computed, type Ref } from 'vue';
import { usePermission } from '@/entities/Permission';
import { DocumentRevisionStatus } from '@/shared/lib';

import type { Homologation } from './homologation.types';
import { useCurrentMemberId } from './useCurrentMemberId';

/**
 * Ações de uma linha da aba Registros da Homologação.
 *
 * São regras próprias, e não as de `useHomologationRowPermissions`: sobre um Registro,
 * `send:Document` não vale, a autoria vem de `manage:RecordManagement` e ela é o que libera a
 * lixeira em qualquer um dos três status, Aguardando VG incluído. O backend decide pelas mesmas
 * regras (`homologation-record-access.ts`), então um ícone desenhado aqui sempre conclui a ação.
 *
 * As permissões se somam, não se excluem: quem criou um Registro e recebeu outro para aprovar age
 * sobre os dois, cada um pela sua regra.
 */
export function useRecordRowPermissions(revision: Ref<Homologation>) {
  const { can } = usePermission();
  const currentMemberId = useCurrentMemberId();

  const canManageRecords = computed(() => can('manage', 'RecordManagement').value);
  const canManageApproval = computed(() => can('manageApproval', 'Homologation').value);
  /** Quem verifica também aprova, por regra hierárquica da história (AC37). */
  const canManageVerification = computed(() => can('manageVerification', 'Homologation').value);

  const currentStatus = computed(() => revision.value.status ?? null);

  const isCreatedByMe = computed(() => {
    // `memberId` é id de membro, não de usuário: comparar com o id do usuário casaria linhas de
    // outra pessoa sempre que os dois números coincidissem.
    const creatorMemberId = revision.value.document?.memberId ?? null;
    return (
      currentMemberId.value !== null &&
      creatorMemberId !== null &&
      Number(creatorMemberId) === Number(currentMemberId.value)
    );
  });

  const isAssignedApproval = computed(() => {
    const managerId = revision.value.approvalManagerId ?? null;
    return (
      currentMemberId.value !== null &&
      managerId !== null &&
      Number(managerId) === Number(currentMemberId.value)
    );
  });

  const isMineByOwnership = computed(() => canManageRecords.value && isCreatedByMe.value);
  const isMineByApproval = computed(() => canManageApproval.value && isAssignedApproval.value);

  const canView = computed(
    () => canManageVerification.value || isMineByOwnership.value || isMineByApproval.value
  );

  /**
   * Só há o que homologar enquanto o Registro está numa etapa de decisão: em Aguardando VG o
   * processo terminou, e o ícone nunca aparece, em nenhuma combinação de permissões (AC25).
   */
  const canHomologate = computed(() => {
    if (currentStatus.value === DocumentRevisionStatus.PENDING_APPROVAL) {
      return canManageVerification.value ? isAssignedApproval.value : isMineByApproval.value;
    }

    if (currentStatus.value === DocumentRevisionStatus.PENDING_VERIFICATION) {
      return canManageVerification.value;
    }

    return false;
  });

  /**
   * Tirar o Registro do fluxo é decisão de quem o criou ou de quem verifica. O gestor de aprovação
   * decide sobre ele, não o remove (AC36).
   */
  const canDelete = computed(() => canManageVerification.value || isMineByOwnership.value);

  return {
    currentStatus,
    isCreatedByMe,
    isAssignedApproval,
    canView,
    canHomologate,
    canDelete,
  };
}
